/** `fitb` names a fill-in-the-blank question's single dropdown_text body mount. */
export type QuestionRegionKind = 'prompt' | 'stem' | 'fitb';

/** The grammar is frozen by the anchor model (persisted from part-4 on); do not vary it. */
export function buildQuestionRegionId(questionId: string, kind: QuestionRegionKind): string {
  return `question:${questionId}:${kind}`;
}

/** One region per answer option, named by its domain id; same frozen grammar. */
export function buildQuestionOptionRegionId(questionId: string, optionId: string): string {
  return `question:${questionId}:option:${optionId}`;
}

/** One region per two-way statement cell, named by its row's domain id; same frozen grammar. */
export function buildQuestionStatementRegionId(questionId: string, rowId: string): string {
  return `question:${questionId}:statement:${rowId}`;
}

/**
 * One region per supplement, named by its domain id; same frozen grammar.
 * Deliberately question-free so marks carry across questions sharing the supplement.
 */
export function buildSupplementRegionId(supplementId: string): string {
  return `supplement:${supplementId}`;
}

/** True when `regionId` names any region of `questionId`. */
export function isQuestionRegionId(regionId: string, questionId: string): boolean {
  return regionId.startsWith(`question:${questionId}:`);
}

const REGION_READING_ORDER: readonly string[] = [
  'prompt',
  'stem',
  'fitb'
] satisfies QuestionRegionKind[];

/** A region's tier in reading order; id-suffixed kinds (option, statement) share the last. */
export function questionRegionRank(regionId: string): number {
  const rank = REGION_READING_ORDER.indexOf(regionId.split(':')[2]);
  return rank === -1 ? REGION_READING_ORDER.length : rank;
}

export type RegionTargetKind = 'question' | 'supplement';

/** What a region's marks are stored against: one question or one supplement. */
export interface RegionTarget {
  kind: RegionTargetKind;
  id: string;
}

/** The target a region id names; null outside the frozen grammar (the server checks the tail). */
export function parseRegionTarget(regionId: string): RegionTarget | null {
  const [scope, id, ...tail] = regionId.split(':');
  if (!id) {
    return null;
  }
  if (scope === 'question' && tail.length > 0 && tail.every((segment) => segment !== '')) {
    return {kind: 'question', id};
  }
  if (scope === 'supplement' && tail.length === 0) {
    return {kind: 'supplement', id};
  }
  return null;
}

export function regionTargetKey({kind, id}: RegionTarget): string {
  return `${kind}:${id}`;
}
